import { User } from 'firebase/auth';
import { FC, useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

import ProcessingOptionsComponent from './ProcessingOptions';
import { useVideoJob } from '../../hooks/useVideoJob';
import { createVideoJob, uploadVideoToGCS } from '../../services/videoApi';
import { ProcessingOptions, PipelineStage, InterpolationBackend } from '../../types';

interface VideoUploadProps {
  user: User;
}

const VideoUpload: FC<VideoUploadProps> = ({ user }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [isCreatingJob, setIsCreatingJob] = useState<boolean>(false);
  const [processingOptions, setProcessingOptions] = useState<ProcessingOptions>({
    stages: ['stabilization' as PipelineStage],
    interpolationBackend: 'film' as InterpolationBackend,
    targetFps: 30,
    inputFpsOverride: null,
    superResolutionScale: 2,
  });
  const [error, setError] = useState<string>('');

  // Use the useVideoJob hook to track job status in real-time
  const { job } = useVideoJob(jobId);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length > 0) {
      setError('Invalid file type or size. Please upload a video file under 20MB.');
      return;
    }

    const video = acceptedFiles[0];
    if (!video) return;

    setFile(video);
    setError('');
    setJobId(null); // Reset job when new file is selected
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024, // 20MB
  });

  const handleUploadAndProcess = async () => {
    if (!file || !user) return;

    setError('');
    setUploadProgress(0);
    setIsCreatingJob(true);

    try {
      // Step 1: Create job and get signed upload URL
      const response = await createVideoJob({
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type,
        stages: processingOptions.stages,
        interpolationBackend: processingOptions.interpolationBackend,
        targetFps: processingOptions.targetFps,
        inputFpsOverride: processingOptions.inputFpsOverride,
        ...(processingOptions.stages.includes('super_resolution') && {
          superResolutionScale: 2 as const,
        }),
      });

      if (!response.uploadUrl) {
        throw new Error('No upload URL received from server');
      }

      setJobId(response.jobId);
      setIsCreatingJob(false);

      // Step 2: Upload video directly to GCS using signed URL
      await uploadVideoToGCS(file, response.uploadUrl, (progress) => {
        setUploadProgress(progress);
      });

      // Step 3: Job status will update automatically via useVideoJob hook
      setUploadProgress(100);

      // Clear file after successful upload
      setFile(null);
    } catch (err) {
      const error = err as Error;
      // Extract more detailed error message
      let errorMessage = error.message;
      if (errorMessage.includes('Failed to create job:')) {
        errorMessage = errorMessage.replace('Failed to create job: ', '');
      }
      setError(`Error: ${errorMessage}`);
      setUploadProgress(0);
      setIsCreatingJob(false);
      setJobId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto mt-8 p-8 card">
      <h2 className="card-header">Upload Video for Processing</h2>

      {!job && (
        <>
          <div {...getRootProps()} className={isDragActive ? 'dropzone-active' : 'dropzone-idle'}>
            <input {...getInputProps()} />
            {file ? (
              <p className="text-gray-700">
                {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
              </p>
            ) : (
              <p className="text-gray-500">
                {isDragActive
                  ? 'Drop the video here'
                  : 'Drag & drop a video, or click to select (max 20MB)'}
              </p>
            )}
          </div>

          <div className="mt-6">
            <ProcessingOptionsComponent
              options={processingOptions}
              onChange={setProcessingOptions}
            />
          </div>

          {isCreatingJob && (
            <div className="mt-6">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                <p className="text-sm text-gray-600">Creating job...</p>
              </div>
            </div>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="mt-6">
              <div className="progress-bar-bg">
                <div className="progress-bar-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Uploading... {uploadProgress.toFixed(0)}%
              </p>
            </div>
          )}

          {error && (
            <div className="alert-danger">
              <p className="alert-danger-text">{error}</p>
            </div>
          )}

          <button
            onClick={handleUploadAndProcess}
            disabled={!file || uploadProgress > 0 || isCreatingJob}
            className="w-full mt-6 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreatingJob ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Preparing upload...
              </span>
            ) : uploadProgress > 0 && uploadProgress < 100 ? (
              `Uploading ${uploadProgress.toFixed(0)}%`
            ) : (
              'Upload and Process'
            )}
          </button>
        </>
      )}

      {job && (
        <div className="border rounded p-6 mt-6 bg-gray-50">
          <h3 className="font-bold text-lg mb-4">Job Status</h3>

          <div className="space-y-2 mb-4">
            <p>
              <span className="font-semibold">Status:</span>{' '}
              <span className="font-mono capitalize">{job.status}</span>
            </p>
            <p>
              <span className="font-semibold">Filename:</span> {job.filename}
            </p>
          </div>

          {job.status === 'pending' && (
            <div className="text-blue-500">
              <p>⏳ Waiting to start...</p>
            </div>
          )}

          {job.status === 'uploading' && (
            <div className="text-blue-500">
              <p>⬆️ Uploading video...</p>
            </div>
          )}

          {job.status === 'processing' && (
            <div className="mt-2">
              <div className="animate-pulse text-blue-500">⚙️ Processing your video...</div>
            </div>
          )}

          {job.status === 'completed' && job.downloadUrl && (
            <div className="mt-4">
              <p className="text-green-500 mb-3 text-lg">✓ Processing complete!</p>
              {job.metrics && (
                <div className="text-sm text-gray-600 mb-4 space-y-1">
                  {job.metrics.processingTime && (
                    <p>Processing time: {job.metrics.processingTime.toFixed(1)}s</p>
                  )}
                  {job.estimatedCost && <p>Estimated cost: ${job.estimatedCost.toFixed(4)}</p>}
                  {job.metrics.outputResolution && (
                    <p>
                      Output: {job.metrics.outputResolution} @ {job.metrics.outputFps}fps
                    </p>
                  )}
                </div>
              )}
              <a
                href={job.downloadUrl}
                download
                className="inline-block px-6 py-3 bg-green-500 text-white rounded hover:bg-green-600 transition"
              >
                Download Processed Video
              </a>
            </div>
          )}

          {job.status === 'failed' && job.error && (
            <div className="mt-4 text-red-500">
              <p className="font-semibold">✗ Processing failed</p>
              <p className="text-sm mt-1">{job.error.message}</p>
            </div>
          )}

          <button
            onClick={() => {
              setJobId(null);
              setUploadProgress(0);
            }}
            className="mt-6 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
          >
            Upload Another Video
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;
